/* Change this file to get your personal Portfolio */

// To change portfolio colors globally go to the  _globalColor.scss file

import emoji from "react-easy-emoji";
import splashAnimation from "./assets/lottie/splashAnimation"; // Rename to your file name for custom animation
import certGenerativeAiLlmsPdf from "./assets/files/Coursera FXMRP3JYZXRU.pdf";
import certSourceSystemsDataIngestionPipelinesPdf from "./assets/files/Coursera SGU5RQ8AUV8U.pdf";
import certDataStorageQueriesPdf from "./assets/files/Coursera B6NZDBEK1YJI.pdf";
import certNlpClassificationVectorSpacesPdf from "./assets/files/Coursera 8LOBWPWJGMRJ.pdf";
import certNlpProbabilisticModelsPdf from "./assets/files/Coursera 0PLZ7B7LUPGL.pdf";
import certNlpSequenceModelsPdf from "./assets/files/Coursera DSJ9EMXF9OTR.pdf";
import certNlpAttentionModelsPdf from "./assets/files/Coursera 9AUXRZ9VT59C.pdf";
import certLinearAlgebraPdf from "./assets/files/Coursera JDHLV01SF900.pdf";
import certMultivariateCalculusPdf from "./assets/files/Coursera IT52HX096ITT.pdf";
import certProbabilityDataScienceAiPdf from "./assets/files/Coursera L6EJHCKTQCMS.pdf";
import certSoftwareDesignLifecyclePdf from "./assets/files/Coursera 4JMNX3NDFO9A.pdf";
import certSoftwareDesignAbstractionPdf from "./assets/files/Coursera QIDZN03UEW7U.pdf";
import certSoftwareDesignMethodsToolsPdf from "./assets/files/Coursera 9PTP08GM8VPG.pdf";
import certSoftwareDesignThreatsMitigationsPdf from "./assets/files/Coursera 4QY22HLPZ9LP.pdf";
import certMlProductionPdf from "./assets/files/coursera-7G6NA4R4UAVZ.pdf";

// Splash Screen

const splashScreen = {
  enabled: true, // set false to disable splash screen
  animation: splashAnimation,
  duration: 2000 // Set animation duration as per your animation
};

// Summary And Greeting Section

const illustration = {
  animated: true // Set to false to use static SVG
};

const greeting = {
  username: "Dmitri Derevjanko",
  title: "Dmitri Derevjanko",
  role: "AI/ML Solutions Engineer",
  lead: "I own complex AI systems from architecture to production.",
  subTitle: emoji(
    "Designing, deploying, and maintaining end-to-end AI, ML, and automation systems for real business workflows."
  ),
  displayGreeting: true, // Set false to hide this section, defaults to true

  // Served from public/resume.pdf: clean URL, same file used by AI agents
  resumeLink: "/resume.pdf",

  // Extra proof lines below main subTitle (see Greeting.js)
  proofHook: {
    lead: "10+ independent public projects you can open and test.",
    confidential:
      "100+ workflows delivered professionally, mostly under confidentiality.",
    detailRest:
      "Public demos show the delivery style: source-aware, useful, measurable, and deployable."
  },
  proofPills: [
    "10+ independent live projects",
    "100+ professional workflows delivered",
    "RAG · agents · automation"
  ]
};

// Social Media Links

const socialMediaLinks = {
  github: "https://github.com/DmitriDerevjanko",
  linkedin: "https://www.linkedin.com/in/dmitri-derevjanko/",
  gmail: "dmitri.derevjanko02@gmail.com",
  facebook: "",
  display: true // Set true to display this section, defaults to false
};

// Skills Section

const skillsSection = {
  title: "What I Build",
  subTitle:
    "AI automation, data products, and business software that move from workflow pain to deployed systems.",
  introTitle: "From messy operations to working software.",
  introText:
    "I map the workflow, design the system, and ship the full product, including APIs, the data layer, the interface, and deployment, adding AI/ML only where it creates measurable value.",
  serviceCards: [
    {
      title: "Fullstack Product Systems",
      desc: "End-to-end products from APIs and data flows to interfaces and deployment, built as one coherent system, not separate frontend and backend pieces.",
      stack: ["Python", "FastAPI", "React", "Databases", "Docker", "Deployment"]
    },
    {
      title: "Business Automation",
      desc: "Workflow tools that reduce manual work: intake, routing, scraping, enrichment, reporting, and handoffs between systems.",
      stack: ["Python", "Playwright", "APIs", "Workers"]
    },
    {
      title: "AI Knowledge Systems",
      desc: "Source-grounded AI tools that search company data, cite context, and make internal knowledge usable.",
      stack: ["LangChain", "Vector DBs", "OpenAI", "FastAPI"]
    },
    {
      title: "Data Products & Dashboards",
      desc: "Dashboards, reports, intelligence workspaces, and decision-support tools built around real user workflows.",
      stack: ["SQL/NoSQL", "Analytics", "Forecasting", "Reports"]
    },
    {
      title: "AI Agents & Workflow Orchestration",
      desc: "Autonomous agents and multi-step AI workflows that route tasks, use tools, make decisions, and execute business processes with guardrails.",
      stack: ["Agents", "Tool Use", "LangChain", "Workflows"]
    },
    {
      title: "AI/ML When It Adds Value",
      desc: "Forecasting, classification, computer vision, explainability, and model-backed workflows packaged as real software.",
      stack: ["Scikit-learn", "LightGBM", "OpenCV", "MLOps"]
    }
  ],
  skills: [
    emoji(
      "⚡ Build AI chatbots and intelligent assistants for customer support and internal tools"
    ),
    emoji(
      "⚡ Implement RAG systems (LangChain, vector databases) for context-aware AI"
    ),
    emoji(
      "⚡ Develop autonomous AI agents for workflow automation and decision-making"
    ),
    emoji(
      "⚡ Create OCR + NLP pipelines for document extraction and data processing"
    ),
    emoji(
      "⚡ Train, evaluate, and package ML models for forecasting, classification, and computer vision demos"
    ),
    emoji(
      "⚡ Turn model outputs into explainable dashboards, confidence signals, and business-facing workflows"
    ),
    emoji(
      "⚡ Build and maintain full AI pipelines: ingestion → embeddings → LLM inference"
    ),
    emoji(
      "⚡ Build fullstack products with Python, FastAPI, React, and deployment-ready architecture"
    ),
    emoji(
      "⚡ Develop dashboards and analytics tools for business insights and ROI tracking"
    ),
    emoji(
      "⚡ Perform data scraping at scale (Selenium / Playwright / API scraping)"
    ),
    emoji("⚡ Deploy services using Docker, CI/CD and server automation")
  ],

  /* Make Sure to include correct Font Awesome Classname to view your icon
   https://fontawesome.com/icons?d=gallery */

  softwareSkills: [
    {skillName: "AI & Machine Learning", fontAwesomeClassname: "fas fa-brain"},
    {
      skillName: "ML Models & MLOps",
      fontAwesomeClassname: "fas fa-project-diagram"
    },
    {skillName: "AI Agents & Automation", fontAwesomeClassname: "fas fa-robot"},
    {skillName: "OpenAI / LLM APIs", fontAwesomeClassname: "fas fa-comments"},
    {
      skillName: "RAG (LangChain / Chroma)",
      fontAwesomeClassname: "fas fa-layer-group"
    },
    {skillName: "Vector Databases", fontAwesomeClassname: "fas fa-search"},
    {skillName: "OCR & Computer Vision", fontAwesomeClassname: "fas fa-eye"},
    {
      skillName: "Forecasting & Predictive Analytics",
      fontAwesomeClassname: "fas fa-chart-line"
    },

    // Fullstack Development
    {skillName: "Python / FastAPI", fontAwesomeClassname: "fas fa-server"},
    {skillName: "API Development", fontAwesomeClassname: "fas fa-plug"},
    {
      skillName: "SQL & NoSQL Databases",
      fontAwesomeClassname: "fas fa-database"
    },
    {skillName: "React / Next.js", fontAwesomeClassname: "fab fa-react"},
    {skillName: "JavaScript / TypeScript", fontAwesomeClassname: "fab fa-js"},
    {skillName: "HTML5 & CSS3", fontAwesomeClassname: "fab fa-html5"},

    // Web Scraping & Automation
    {
      skillName: "Web Scraping (Selenium / Playwright)",
      fontAwesomeClassname: "fas fa-spider"
    },
    {skillName: "Automation Pipelines", fontAwesomeClassname: "fas fa-tools"},

    // Data Analytics & Visualization
    {
      skillName: "Data Analysis (Python / R)",
      fontAwesomeClassname: "fas fa-chart-line"
    },
    {
      skillName: "Dashboards & Visualization",
      fontAwesomeClassname: "fas fa-chart-area"
    },

    // DevOps
    {skillName: "Cloud / VPS", fontAwesomeClassname: "fas fa-cloud"},
    {skillName: "Linux / Server Ops", fontAwesomeClassname: "fab fa-linux"},
    {skillName: "Docker & Deployment", fontAwesomeClassname: "fab fa-docker"},
    {skillName: "CI/CD (GitHub Actions)", fontAwesomeClassname: "fas fa-sync"}
  ],
  display: true // Set false to hide this section, defaults to true
};

// Education Section

const educationInfo = {
  display: true, // Set false to hide this section, defaults to true
  schools: [
    {
      schoolName: "Tallinn University of Technology",
      logo: require("./assets/images/taltech_logo.png"),
      subHeader: "Master of Science in Business Information Technology",
      duration: "September 2025 – ongoing, alongside professional work",
      desc: "Additional master's studies in business information systems, pursued in parallel with full-time work at AIRE and Adapter.",
      descBullets: [
        "Thesis direction: automation in business workflows.",
        "Relevant focus: enterprise analytics, IT strategy, and AI-enabled business tools."
      ]
    },
    {
      schoolName: "Tallinn University of Technology",
      logo: require("./assets/images/taltech_logo.png"),
      subHeader: "Bachelor of Science in Business Information Technology",
      duration: "September 2021 – June 2025",
      desc: "Completed a technical business IT program covering software development, databases, networking, and IT infrastructure.",
      diplomaLabel: "View Bachelor Diploma",
      diplomaModalTitle: "Bachelor Diploma",
      diplomaLink: require("./assets/images/bachelor-diploma-2025.jpg"),
      descBullets: [
        "Built a foundation in programming, data structures, algorithms, and database systems.",
        "Applied the degree through OCR, RAG, and automation work in real projects."
      ]
    }
  ]
};

// Proficiency bars: how my practice actually breaks down in production work
const techStack = {
  viewSkillBars: true, // Set to true to show Proficiency Section
  sectionTitle: "Where I Create Business Value",
  sectionSubtitle:
    "I turn messy workflows, scattered data, and manual operations into faster, more reliable fullstack software: APIs, automation pipelines, dashboards, internal tools, and AI/ML where it creates measurable value.",
  experience: [
    {
      Stack: "AI / ML & RAG Integration",
      level: "Core strength",
      progressWidth: "100%",
      caption:
        "I build assistants and ML-backed tools that answer from real data, cite sources, forecast outcomes, and reduce repetitive expert work."
    },
    {
      Stack: "Machine Learning & Model Evaluation",
      level: "Core strength",
      progressWidth: "100%",
      caption:
        "I package practical models into usable products: forecasting, classification, computer vision, explainability, confidence labels, and validation metrics."
    },
    {
      Stack: "Web Scraping & Automation",
      level: "Core strength",
      progressWidth: "100%",
      caption:
        "I automate manual data collection and repetitive workflows, even when there is no clean API or ready-made integration."
    },
    {
      Stack: "Fullstack Product Development",
      level: "Core strength",
      progressWidth: "100%",
      caption:
        "I ship complete products end to end: APIs, data layer, interfaces, integrations, and deployment, so teams get one working system, not disconnected pieces."
    },
    {
      Stack: "Databases & Data Layer",
      level: "Production experience",
      progressWidth: "94%",
      caption:
        "I organize business and product data so it stays consistent, searchable, auditable, and useful in daily work."
    },
    {
      Stack: "DevOps & Deployment",
      level: "Production experience",
      progressWidth: "94%",
      caption:
        "I deploy fullstack tools as real services with stable releases, monitoring-friendly setup, and fewer works-on-my-machine problems."
    },
    {
      Stack: "Financial Analytics",
      level: "Production experience",
      progressWidth: "94%",
      caption:
        "I combine external data, business logic, model signals, and AI summaries into dashboards and reports that help teams make faster decisions."
    }
  ],
  displayCodersrank: false
};

// Work experience section

const workExperiences = {
  display: true, // Set true to show Work Experience Section
  experience: [
    {
      role: "Freelance AI Integration Specialist",
      company: "Self-Employed",
      companylogo: require("./assets/images/9495859.png"),
      date: "September 2021 – Present",
      desc: "Build practical fullstack AI/ML and automation products for small and medium-sized businesses, from first workflow mapping to deployed demos and internal tools.",
      keyWork: ["RAG systems", "ML demos", "OCR workflows", "Automation"],
      descBullets: [
        "Implemented chatbots for customer support, document classification tools, and automated reporting dashboards.",
        "Architected a fullstack RAG product that ingests data, creates embeddings, and serves LLM-generated responses through APIs and a React/Next.js interface.",
        "Built ML-backed demos for forecasting, classification, and decision support where model outputs are exposed through clear dashboards and API contracts.",
        "Optimized inference performance by quantizing LLaMA models with llama.cpp, achieving significantly faster response times on CPU-only servers.",
        "Conduct workshops on AI strategy, ROI analysis, and best practices for in-house teams."
      ]
    },
    {
      role: "Digitalization and Data Management Expert",
      company: "AI & Robotics Estonia",
      companylogo: require("./assets/images/AIRE_logo_black_background.png"),
      date: "March 2024 – Present",
      desc: "Design and implement fullstack AI/ML automation workflows, OCR-based document processing, RAG systems, dashboards, and deployable data products for enterprise clients.",
      keyWork: ["Document automation", "RAG", "ML workflows", "FastAPI"],
      descBullets: [
        "Built document ingestion workflows that process hundreds of PDFs per day using OCR, extraction logic, and vector search, reducing manual classification time by 70%.",
        "Designed and deployed FastAPI services for internal AI assistants connected to company knowledge bases.",
        "Implemented RAG pipelines with LangChain and Chroma for CRM and document-search use cases.",
        "Turned model outputs, data quality checks, and business rules into dashboards and decision-support workflows for practical digitalization use cases.",
        "Containerized automation services with Docker and set up CI/CD pipelines, decreasing deployment time from days to minutes."
      ]
    },
    {
      role: "AI Solutions Engineer",
      company: "Adapter",
      companylogo: require("./assets/images/5866160393120582473.jpg"),
      date: "October 2025 – Present",
      desc: "Build fullstack AI/ML and automation systems for Adapter, focused on service matching, partner routing, CRM structure, and cleaner daily operations.",
      keyWork: [
        "1500+ services",
        "27 partners",
        "CRM automation",
        "Embeddings"
      ],
      descBullets: [
        "Created an AI Agent that uses an embedding-based knowledge base (1500+ services) to automatically find the best-fit innovation and research services for companies.",
        "Built a partner-matching module that identifies the most relevant expert from a network of 27 partner universities and research organisations.",
        "Designed and implemented a unified CRM system in Notion for 27 partners with role-based access, synced databases, automated workflows, and clean data structure.",
        "Developed fullstack logic for data ingestion, embeddings generation, scoring, and AI-driven recommendations to support fast, accurate matchmaking.",
        "Optimized AI workflows and implemented automation pipelines to streamline daily work for Adapter and AIRE teams."
      ]
    }
  ]
};

const professionalWorkflowExamples = {
  title: "What 100+ Professional Workflows Means",
  subtitle:
    "Three anonymized examples of production work. They show scope, ownership, and operational impact without exposing confidential client systems.",
  examples: [
    {
      label: "Document operations",
      title: "Document Processing Automation",
      system:
        "OCR, extraction, classification, and vector-search workflows for high-volume PDF processing.",
      ownership:
        "Owned solution architecture, FastAPI services, deployment, monitoring, and operational handoff.",
      impact:
        "Processes hundreds of PDFs per day and reduced manual classification time by 70%."
    },
    {
      label: "Applied AI",
      title: "AI-Based Service Matching",
      system:
        "Embedding-based retrieval and scoring across 1,500+ innovation and research services.",
      ownership:
        "Designed ingestion, embeddings, matching logic, recommendation flow, and the usable product layer.",
      impact:
        "Routes company needs across a network of 27 partner universities and research organisations."
    },
    {
      label: "Operational systems",
      title: "Multi-Partner CRM Automation",
      system:
        "A unified CRM with synchronized databases, role-based access, routing, and automated workflows.",
      ownership:
        "Designed the data model, permissions, automation rules, and operational rollout.",
      impact:
        "Created one working structure for daily collaboration across 27 partner organisations."
    }
  ]
};

/* Your Open Source Section to View Your Github Pinned Projects
   To know how to get github key look at readme.md */

const openSource = {
  showGithubProfile: "true", // Set true or false to show Contact profile using Github, defaults to true
  // Pinned repos section hidden (empty / no public pins); GitHub profile card still uses showGithubProfile below.
  display: false
};

// Some big projects you have worked on

const bigProjects = {
  title: "Flagship Case Studies",
  subtitle:
    "FOUR DEEP CASE STUDIES IN AI SYSTEMS, APPLIED ML, AND COMPUTER VISION",
  projects: [
    /*
    {
      image: require("./assets/images/images.png"),
      projectName: "ROI AI Agent",
      projectDesc:
        "An AI-driven agent that calculates return on investment by analyzing financial inputs and generating actionable insights for business decision-making.",
      footerLink: [
        {
          name: "Open Demo",
          url: "https://aire-roi-agent.ee/"
        }
      ]
    },
    */
    /*
    {
      image: require("./assets/images/AIRE_logo_black_background1.png"),
      projectName: "🤖AI & Robotics Estonia (AIRE)",
      projectDesc:
        "The official website of AI & Robotics Estonia (AIRE), featuring AI initiatives, resources, and collaboration opportunities to drive digital transformation in Estonian industry.",
      footerLink: [
        {
          name: "Visit Website",
          url: "https://aire-edih.eu/"
        }
      ]
    },
    */
    {
      image: require("./assets/images/law-agent-dashboard-card.webp"),
      imageSmall: require("./assets/images/law-agent-dashboard-card-400.webp"),
      projectName: "Law Agent",
      projectDesc:
        "A source-backed legal AI assistant for Estonian law, built to answer with citations, reduce generic model risk, and make legal research faster.",
      impactLabels: [
        "181k chunks",
        "16k+ legal records",
        "100% indexed law corpus"
      ],
      footerLink: [
        {
          name: "Open Demo",
          url: "https://counsel.dmitriderevjanko.com/"
        }
      ]
    },
    {
      image: require("./assets/images/marketplace-ai-analytics-card.webp"),
      imageSmall: require("./assets/images/marketplace-ai-analytics-card-400.webp"),
      projectName: "Marketplace Intelligence",
      projectDesc:
        "A multi-model marketplace intelligence platform where 6 ML components work together to recommend prices, estimate sale probability, forecast demand, explain decisions, and expose real training-data lineage.",
      impactLabels: [
        "6-model ML system",
        "1.47M pricing listings",
        "5M MerRec events"
      ],
      footerLink: [
        {
          name: "Open Demo",
          url: "https://pricingiq.dmitriderevjanko.com/"
        }
      ]
    },
    {
      image: require("./assets/images/osint-radar-card.webp"),
      imageSmall: require("./assets/images/osint-radar-card-400.webp"),
      projectName: "OSINT Radar",
      projectDesc:
        "Consolidates 31 public-data integrations into one situational-awareness dashboard, with scheduled sweeps, source-health monitoring, alert scoring, change detection, and optional AI-assisted analysis.",
      impactLabels: [
        "31 integrations",
        "15-minute sweeps",
        "SSE live updates"
      ],
      footerLink: [
        {
          name: "Open Demo",
          url: "https://osint.dmitriderevjanko.com/"
        }
      ]
    },
    {
      image: require("./assets/images/oncoview-mammography-card.webp"),
      imageSmall: require("./assets/images/oncoview-mammography-card-400.webp"),
      projectName: "OncoView AI: Breast Cancer Imaging Triage",
      projectDesc:
        "An explainable breast imaging AI demo for testing triage predictions, visual evidence, and high-recall screening behavior in one interface.",
      impactLabels: [
        "6-model ensemble",
        "Grad-CAM explainability",
        "Balanced + high-recall modes"
      ],
      footerLink: [
        {
          name: "Open Demo",
          url: "https://oncoview.dmitriderevjanko.com/"
        },
        {
          name: "View on GitHub",
          url: "https://github.com/DmitriDerevjanko/cancer_detector"
        }
      ]
    },
    {
      image: require("./assets/images/ai-risk-card.webp"),
      imageSmall: require("./assets/images/ai-risk-card-400.webp"),
      projectName: "Global Risk Forecast Dashboard",
      projectDesc:
        "A forecasting dashboard that turns historical incident data into regional risk views for faster scenario exploration and monitoring.",
      impactLabels: [
        "LightGBM forecasting",
        "GTD dataset",
        "Interactive risk map"
      ],
      footerLink: [
        {
          name: "Open Demo",
          url: "https://ai-risk.dmitriderevjanko.com"
        },
        {
          name: "View on GitHub",
          url: "https://github.com/DmitriDerevjanko/ai-risk-clean"
        }
      ]
    },
    {
      image: require("./assets/images/Computer-Vision-in-Manufacturing.webp"),
      imageSmall: require("./assets/images/Computer-Vision-in-Manufacturing-400.webp"),
      projectName: "SteelVision: Surface Defect Segmentation",
      projectDesc:
        "A computer vision demo that localizes steel surface defects at pixel level, helping quality teams inspect issues instead of only classifying images.",
      impactLabels: [
        "UNet++ / EfficientNet",
        "Pixel masks",
        "FastAPI inference"
      ],
      footerLink: [
        {
          name: "Open Demo",
          url: "https://ai-vision.dmitriderevjanko.com"
        },
        {
          name: "View on GitHub",
          url: "https://github.com/DmitriDerevjanko/ai-vision"
        }
      ]
    },
    {
      image: require("./assets/images/ai-smartcam-card.webp"),
      imageSmall: require("./assets/images/ai-smartcam-card-400.webp"),
      projectName: "SmartCam: Live Object Detection",
      projectDesc:
        "A real-time object detection demo that turns a live video stream into readable detections for browser-based monitoring and testing.",
      impactLabels: [
        "YOLOv8 live",
        "Real-time video stream",
        "FastAPI + OpenCV"
      ],
      footerLink: [
        {
          name: "Open Demo",
          url: "https://ai-news.dmitriderevjanko.com"
        },
        {
          name: "View on GitHub",
          url: "https://github.com/DmitriDerevjanko/ai-news"
        }
      ]
    },
    {
      image: require("./assets/images/financelens-analysis-setup.png"),
      imageSmall: require("./assets/images/financelens-analysis-setup-400.png"),
      projectName: "FinanceLens Analyst",
      projectDesc:
        "A financial analysis workspace that pulls filings, market data, and identifiers into one auditable autofill flow for faster valuation prep.",
      impactLabels: [
        "SEC + Yahoo + OpenFIGI",
        "AI autofill",
        "Source-linked data"
      ],
      footerLink: [
        {
          name: "Open Demo",
          url: "https://finance.dmitriderevjanko.com/"
        }
      ]
    },
    {
      image: require("./assets/images/business_register.jpg"),
      imageSmall: require("./assets/images/business_register-400.jpg"),
      projectName: "Estonian Company Intelligence Hub",
      projectDesc:
        "A company intelligence workspace that combines registry data, web signals, risk scoring, AI summaries, and PDF reports for due diligence.",
      impactLabels: [
        "13 profile sections",
        "0-100 risk scoring",
        "PDF reports"
      ],
      footerLink: [
        {
          name: "Open Demo",
          url: "https://intel.dmitriderevjanko.com/"
        }
      ]
    },
    {
      image: require("./assets/images/signaldesk-ois-card.webp"),
      imageSmall: require("./assets/images/signaldesk-ois-card-400.webp"),
      projectName: "Opportunity Intelligence Studio",
      projectDesc:
        "A multi-agent research workspace for Baltic and Nordic accounts that ranks targets, maps pain points, estimates ROI, and drafts outreach with evidence and confidence labels.",
      impactLabels: [
        "8 specialized agents",
        "27 account dossiers",
        "Evidence-linked briefs"
      ],
      footerLink: [
        {
          name: "Open Demo",
          url: "https://signaldesk.dmitriderevjanko.com/"
        }
      ]
    }
  ],

  display: true
};

// Achievement Section
// Include certificates, talks etc

const achievementSection = {
  title: emoji("Credentials & Certifications"),
  subtitle:
    "Selected certifications and training in AI, machine learning, and applied software development.",
  featuredCredentialIds: [
    "FXMRP3JYZXRU",
    "7G6NA4R4UAVZ",
    "SGU5RQ8AUV8U",
    "B6NZDBEK1YJI",
    "DSJ9EMXF9OTR",
    "9AUXRZ9VT59C",
    "L6EJHCKTQCMS",
    "4QY22HLPZ9LP"
  ],
  achievementsCards: [
    {
      title: "Generative AI with Large Language Models",
      subtitle:
        "DeepLearning.AI and AWS course on the generative AI project lifecycle, LLM training, fine-tuning, evaluation, and deployment patterns.",
      imageAlt: "DeepLearning.AI and AWS Logo",
      logoText: "DeepLearning.AI",
      logoLabel: "AWS",
      credentialId: "FXMRP3JYZXRU",
      skills: [
        "Generative AI",
        "Large Language Models",
        "Fine-tuning",
        "Model Evaluation",
        "Model Deployment",
        "Reinforcement Learning",
        "Python",
        "LLM Applications"
      ],
      certificatePdf: certGenerativeAiLlmsPdf,
      verificationUrl:
        "https://www.coursera.org/account/accomplishments/verify/FXMRP3JYZXRU"
    },
    {
      title: "Machine Learning in Production",
      subtitle:
        "DeepLearning.AI course on ML project lifecycle, production pipelines, deployment patterns, monitoring, and data-centric model improvement.",
      imageAlt: "DeepLearning.AI Logo",
      logoText: "DeepLearning.AI",
      credentialId: "7G6NA4R4UAVZ",
      skills: [
        "MLOps",
        "Model Evaluation",
        "Continuous Monitoring",
        "Continuous Deployment",
        "Data Quality",
        "Data Preprocessing",
        "Model Training",
        "Application Deployment"
      ],
      certificatePdf: certMlProductionPdf,
      verificationUrl:
        "https://www.coursera.org/account/accomplishments/verify/7G6NA4R4UAVZ"
    },
    {
      title: "Source Systems, Data Ingestion, and Pipelines",
      subtitle:
        "DeepLearning.AI and AWS course on translating stakeholder needs into data system requirements, building batch and streaming ingestion on AWS, and integrating security, orchestration, and data management.",
      imageAlt: "DeepLearning.AI and AWS Logo",
      logoText: "DeepLearning.AI",
      logoLabel: "AWS",
      credentialId: "SGU5RQ8AUV8U",
      skills: [
        "Data Pipelines",
        "Data Ingestion",
        "Amazon Web Services",
        "Amazon CloudWatch",
        "Data Processing",
        "Data Transformation",
        "Extract, Transform, Load",
        "Data Quality",
        "Infrastructure as Code",
        "CI/CD",
        "IT Automation",
        "Terraform",
        "Network Troubleshooting"
      ],
      certificatePdf: certSourceSystemsDataIngestionPipelinesPdf,
      verificationUrl:
        "https://www.coursera.org/account/accomplishments/verify/SGU5RQ8AUV8U"
    },
    {
      title: "Data Storage and Queries",
      subtitle:
        "DeepLearning.AI and AWS course on designing storage architectures for different use cases, selecting appropriate data technologies, and optimizing query patterns for performance and value.",
      imageAlt: "DeepLearning.AI and AWS Logo",
      logoText: "DeepLearning.AI",
      logoLabel: "AWS",
      credentialId: "B6NZDBEK1YJI",
      skills: [
        "Data Storage",
        "Data Storage Technologies",
        "Database Systems",
        "Databases",
        "Data Warehousing",
        "Vector Databases",
        "Cloud Storage",
        "Amazon Web Services",
        "Query Languages",
        "File Systems",
        "Performance Tuning"
      ],
      certificatePdf: certDataStorageQueriesPdf,
      verificationUrl:
        "https://www.coursera.org/account/accomplishments/verify/B6NZDBEK1YJI"
    },
    {
      title:
        "Natural Language Processing with Classification and Vector Spaces",
      subtitle:
        "DeepLearning.AI course covering text classification, vector spaces, embeddings, logistic regression, Naive Bayes, and word-vector methods for NLP systems.",
      imageAlt: "DeepLearning.AI Logo",
      logoText: "DeepLearning.AI",
      credentialId: "8LOBWPWJGMRJ",
      skills: [
        "Natural Language Processing",
        "Text Classification",
        "Vector Spaces",
        "Embeddings",
        "Logistic Regression",
        "Naive Bayes",
        "Text Mining"
      ],
      certificatePdf: certNlpClassificationVectorSpacesPdf,
      verificationUrl:
        "https://www.coursera.org/account/accomplishments/verify/8LOBWPWJGMRJ"
    },
    {
      title: "Natural Language Processing with Probabilistic Models",
      subtitle:
        "DeepLearning.AI course covering probabilistic NLP methods, dynamic programming, hidden Markov models, autocomplete, autocorrect, embeddings, and part-of-speech tagging.",
      imageAlt: "DeepLearning.AI Logo",
      logoText: "DeepLearning.AI",
      credentialId: "0PLZ7B7LUPGL",
      skills: [
        "Natural Language Processing",
        "Probabilistic Models",
        "Hidden Markov Models",
        "Dynamic Programming",
        "Embeddings",
        "Autocorrect",
        "Part-of-Speech Tagging"
      ],
      certificatePdf: certNlpProbabilisticModelsPdf,
      verificationUrl:
        "https://www.coursera.org/account/accomplishments/verify/0PLZ7B7LUPGL"
    },
    {
      title: "Natural Language Processing with Sequence Models",
      subtitle:
        "DeepLearning.AI course on recurrent neural networks (RNNs, LSTMs, GRUs), and Siamese architectures in TensorFlow for sentiment analysis, text generation, and named entity recognition.",
      imageAlt: "DeepLearning.AI Logo",
      logoText: "DeepLearning.AI",
      credentialId: "DSJ9EMXF9OTR",
      skills: [
        "Natural Language Processing",
        "Recurrent Neural Networks",
        "TensorFlow",
        "Embeddings",
        "Named Entity Recognition",
        "Text Generation",
        "Sentiment Analysis",
        "Deep Learning"
      ],
      certificatePdf: certNlpSequenceModelsPdf,
      verificationUrl:
        "https://www.coursera.org/account/accomplishments/verify/DSJ9EMXF9OTR"
    },
    {
      title: "Natural Language Processing with Attention Models",
      subtitle:
        "DeepLearning.AI course covering encoder-decoder models, causal attention, self-attention, and transformer-based methods for machine translation, summarization, and question answering.",
      imageAlt: "DeepLearning.AI Logo",
      logoText: "DeepLearning.AI",
      credentialId: "9AUXRZ9VT59C",
      skills: [
        "Natural Language Processing",
        "Attention Models",
        "Transformers",
        "Encoder-Decoder Models",
        "Machine Translation",
        "Text Summarization",
        "Question Answering",
        "Deep Learning"
      ],
      certificatePdf: certNlpAttentionModelsPdf,
      verificationUrl:
        "https://www.coursera.org/account/accomplishments/verify/9AUXRZ9VT59C"
    },
    {
      title: "Mathematics for Machine Learning: Linear Algebra",
      subtitle:
        "Imperial College London course covering the linear algebra foundation behind machine learning models, data transformations, and vector-based methods.",
      imageAlt: "Imperial College London Logo",
      logoText: "Imperial",
      logoLabel: "College London",
      credentialId: "JDHLV01SF900",
      skills: [
        "Linear Algebra",
        "Applied Mathematics",
        "Machine Learning",
        "Data Transformation",
        "Data Manipulation",
        "Jupyter"
      ],
      certificatePdf: certLinearAlgebraPdf,
      verificationUrl:
        "https://www.coursera.org/account/accomplishments/verify/JDHLV01SF900"
    },
    {
      title: "Mathematics for Machine Learning: Multivariate Calculus",
      subtitle:
        "Imperial College London course covering calculus foundations for machine learning, including derivatives, optimization, and mathematical tools for model training.",
      imageAlt: "Imperial College London Logo",
      logoText: "Imperial",
      logoLabel: "College London",
      credentialId: "IT52HX096ITT",
      skills: [
        "Calculus",
        "Derivatives",
        "Numerical Analysis",
        "Model Optimization",
        "Applied Mathematics",
        "Machine Learning"
      ],
      certificatePdf: certMultivariateCalculusPdf,
      verificationUrl:
        "https://www.coursera.org/account/accomplishments/verify/IT52HX096ITT"
    },
    {
      title: "Probability Foundations for Data Science and AI",
      subtitle:
        "University of Colorado Boulder course covering probability foundations for statistics, data science, and AI, including conditional probability, random variables, and distributions.",
      imageAlt: "University of Colorado Boulder Logo",
      logoText: "CU",
      logoLabel: "Boulder",
      credentialId: "L6EJHCKTQCMS",
      skills: [
        "Probability",
        "Statistics",
        "Data Analysis",
        "Statistical Analysis",
        "Bayesian Statistics",
        "Probability Distribution"
      ],
      certificatePdf: certProbabilityDataScienceAiPdf,
      verificationUrl:
        "https://www.coursera.org/account/accomplishments/verify/L6EJHCKTQCMS"
    },
    {
      title:
        "Software Design as an Element of the Software Development Lifecycle",
      subtitle:
        "University of Colorado Boulder course covering software design within the development lifecycle, including requirements analysis, design methodology, security considerations, and SDLC models.",
      imageAlt: "University of Colorado Boulder Logo",
      logoText: "CU",
      logoLabel: "Boulder",
      credentialId: "4JMNX3NDFO9A",
      skills: [
        "Software Design",
        "Software Development Life Cycle",
        "Requirements Analysis",
        "Application Security",
        "Agile Methodology",
        "Waterfall Methodology"
      ],
      certificatePdf: certSoftwareDesignLifecyclePdf,
      verificationUrl:
        "https://www.coursera.org/account/accomplishments/verify/4JMNX3NDFO9A"
    },
    {
      title: "Software Design as an Abstraction",
      subtitle:
        "University of Colorado System course covering abstraction in software design, including design strategies, security, databases, usability, and user interface design considerations.",
      imageAlt: "University of Colorado Boulder Logo",
      logoText: "CU",
      logoLabel: "Boulder",
      credentialId: "QIDZN03UEW7U",
      skills: [
        "Software Design",
        "Design Strategies",
        "Software Design Patterns",
        "Application Security",
        "Database Design",
        "Relational Databases",
        "Usability Testing",
        "User Interface Design"
      ],
      certificatePdf: certSoftwareDesignAbstractionPdf,
      verificationUrl:
        "https://www.coursera.org/account/accomplishments/verify/QIDZN03UEW7U"
    },
    {
      title: "Software Design Methods and Tools",
      subtitle:
        "University of Colorado System course covering practical software design methods and engineering tools, including UML, diagram design, data flow diagrams, testing, and test automation.",
      imageAlt: "University of Colorado Boulder Logo",
      logoText: "CU",
      logoLabel: "Boulder",
      credentialId: "9PTP08GM8VPG",
      skills: [
        "Software Design",
        "Software Design Methods",
        "Unified Modeling Language",
        "Diagram Design",
        "Data Flow Diagrams",
        "Software Testing",
        "Unit Testing",
        "Test Automation",
        "Software Engineering Tools"
      ],
      certificatePdf: certSoftwareDesignMethodsToolsPdf,
      verificationUrl:
        "https://www.coursera.org/account/accomplishments/verify/9PTP08GM8VPG"
    },
    {
      title: "Software Design Threats and Mitigations",
      subtitle:
        "University of Colorado System course covering software design security, threat modeling, vulnerability assessment, mitigation strategies, cryptography, and secure coding practices.",
      imageAlt: "University of Colorado Boulder Logo",
      logoText: "CU",
      logoLabel: "Boulder",
      credentialId: "4QY22HLPZ9LP",
      skills: [
        "Software Architecture",
        "Threat Modeling",
        "Threat Management",
        "Vulnerability Assessments",
        "Vulnerability Management",
        "Application Security",
        "Security Engineering",
        "Secure Coding",
        "Cryptography",
        "Public Key Cryptography Standards",
        "Software Design Patterns",
        "Software Visualization"
      ],
      certificatePdf: certSoftwareDesignThreatsMitigationsPdf,
      verificationUrl:
        "https://www.coursera.org/account/accomplishments/verify/4QY22HLPZ9LP"
    }
  ],
  display: true
};

const contactInfo = {
  title: emoji("Start a Project"),
  subtitle:
    "Have a workflow, internal tool, dashboard, data product, or AI idea that should become real software? Send me the process, tools, and bottleneck. I’ll help map the fastest path to a working system.",
  projectPrompts: [
    "What workflow or decision is slow today?",
    "Which tools, files, websites, or databases are involved?",
    "What would a useful first version need to do?"
  ],
  number: "+37253520920",
  email_address: "dmitri.derevjanko02@gmail.com",
  display: true
};

const roiCalculator = {
  display: true,
  title: "ROI Calculator",
  subtitle:
    "Estimate how much time and money a workflow tool, automation, dashboard, or AI-enhanced system could save every month.",
  assumptions:
    "Assumption: 4.33 weeks per month. Figures are directional estimates.",
  defaultValues: {
    hoursPerWeek: 35,
    hourlyRate: 30,
    automationPercent: 45,
    errorsPerMonth: 12,
    costPerError: 80,
    errorReductionPercent: 50,
    implementationCost: 3500
  },
  presets: [
    {
      key: "small",
      label: "Small team",
      description: "5-12 people",
      values: {
        hoursPerWeek: 24,
        hourlyRate: 28,
        automationPercent: 35,
        errorsPerMonth: 8,
        costPerError: 60,
        errorReductionPercent: 40,
        implementationCost: 2200
      }
    },
    {
      key: "mid",
      label: "Mid-size ops",
      description: "20-60 people",
      values: {
        hoursPerWeek: 60,
        hourlyRate: 30,
        automationPercent: 55,
        errorsPerMonth: 18,
        costPerError: 95,
        errorReductionPercent: 55,
        implementationCost: 4200
      }
    },
    {
      key: "enterprise",
      label: "Enterprise",
      description: "100+ people",
      values: {
        hoursPerWeek: 150,
        hourlyRate: 42,
        automationPercent: 62,
        errorsPerMonth: 45,
        costPerError: 120,
        errorReductionPercent: 60,
        implementationCost: 12000
      }
    }
  ]
};

const isHireable = true;

export {
  illustration,
  greeting,
  socialMediaLinks,
  splashScreen,
  skillsSection,
  educationInfo,
  techStack,
  workExperiences,
  professionalWorkflowExamples,
  openSource,
  bigProjects,
  achievementSection,
  contactInfo,
  roiCalculator,
  isHireable
};
